import * as React from 'react';
import List from '@mui/material/List';
import { formatDate } from '../../../../../shared/LanguageService';
import TimelineCalendarDropZone from './TimelineCalendarDropZone';
import { CalendarRowSize, Photographer } from '../../BookingsCalendarTypes';
import useCreateSlotType from '../../state/useCreateSlotType';
import {
    getBusinessHours,
    getFiveMinutesSlots,
    getSlotIndex,
    getWorkHoursForDay,
    isDayClosed
} from './timelineCalendarHelperFuncitons';
import Box from '@mui/system/Box';

type TimelineCalendarGridProps = {
    photographer: Photographer;
    newBookingLenght: number;
    rowIndex: number;
    onDrop: (photographer: Photographer, index: number, isResized) => void;
    selectedDate: Date;
    hideTravelTimes: boolean;
    preventResize: boolean;
    isResizing: boolean;
    hasNewOrderInCalendar: boolean;
    hasNoEventsInCalendar: boolean;
    isActiveRow: boolean;
    onActiveRow: (activeRow: number) => void;
};

const listStyle = (hasNewOrderInCalendar, hasNoEventsInCalendar, isActiveRow) => {
    return {
        display: 'contents',
        height:
            hasNewOrderInCalendar || isActiveRow
                ? CalendarRowSize.large
                : hasNoEventsInCalendar
                ? CalendarRowSize.small
                : CalendarRowSize.medium
    };
};

function getDateFromHours(time, selectedDate) {
    time = time.split(':');
    let now = new Date(selectedDate);
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), ...time);
}

const TimelineCalendarGrid = React.forwardRef(
    (
        {
            photographer,
            newBookingLenght,
            rowIndex,
            onDrop,
            selectedDate,
            hideTravelTimes,
            preventResize,
            isResizing,
            hasNewOrderInCalendar,
            hasNoEventsInCalendar,
            isActiveRow,
            onActiveRow
        }: TimelineCalendarGridProps,
        ref
    ) => {
        let businessHours = selectedDate && getBusinessHours(selectedDate);
        let fiveMinuteTimeSlots = selectedDate && getFiveMinutesSlots(selectedDate);

        let workHoursForDay = getWorkHoursForDay(photographer.workingHours, selectedDate);
        let dayClosed = isDayClosed(photographer.workingHours, selectedDate);

        let workHourStart = workHoursForDay && workHoursForDay.isAvailable
            ? getDateFromHours(workHoursForDay.start, selectedDate)
            : null;
        let workHourEnd = workHoursForDay && workHoursForDay.isAvailable
            ? getDateFromHours(workHoursForDay.end, selectedDate)
            : null;

        let workHourStartIndex: number =
            workHourStart && Math.round(getSlotIndex(businessHours[0], workHourStart));

        let workHourEndIndex: number =
            workHourEnd && Math.round(getSlotIndex(businessHours[0], workHourEnd));

        return (
            <List
                key={
                    photographer.id +
                    newBookingLenght +
                    `${hideTravelTimes}` +
                    hasNewOrderInCalendar
                }
                sx={listStyle(hasNewOrderInCalendar, hasNoEventsInCalendar, isActiveRow)}
            >
                {fiveMinuteTimeSlots &&
                    businessHours &&
                    fiveMinuteTimeSlots.map((workHour, index) => {
                        let slotIndex = index + 1;
                        let formattedWorkHour: string = formatDate(workHour, 'kk' + ':' + 'mm');
                        let hideLastSlot: boolean = fiveMinuteTimeSlots.length === slotIndex;
                        let lastSlot = index === fiveMinuteTimeSlots.length - 2;

                        let { isAvailableForDrop, isAvailableToResize } = useCreateSlotType({
                            index,
                            photographer,
                            newBookingLenght,
                            businessHours,
                            hideTravelTimes
                        });

                        let isOutsideWorkHours: boolean =
                            dayClosed || index < workHourStartIndex || index >= workHourEndIndex;

                        return !hideLastSlot ? (
                            <Box
                                key={
                                    'slot-type' +
                                    index +
                                    hasNewOrderInCalendar +
                                    hasNoEventsInCalendar
                                }
                                sx={{
                                    backgroundColor: isOutsideWorkHours ? '#EEEEEE' : '',
                                    height:
                                        hasNewOrderInCalendar || isActiveRow
                                            ? CalendarRowSize.large
                                            : hasNoEventsInCalendar
                                            ? CalendarRowSize.small
                                            : CalendarRowSize.medium
                                }}
                            >
                                <TimelineCalendarDropZone
                                    key={index}
                                    formattedDisplayTime={formattedWorkHour}
                                    index={slotIndex}
                                    onDrop={onDrop}
                                    photographer={photographer}
                                    rowIndex={rowIndex}
                                    availableForDrop={isAvailableForDrop}
                                    lastSlot={lastSlot}
                                    isAvailableForResize={isAvailableToResize}
                                    preventResize={preventResize}
                                    isResizing={isResizing}
                                    hasNewOrderInCalendar={hasNewOrderInCalendar}
                                    hasNoEventsInCalendar={hasNoEventsInCalendar}
                                    isActiveRow={isActiveRow}
                                    onActiveRow={onActiveRow}
                                />
                            </Box>
                        ) : null;
                    })}
            </List>
        );
    }
);

export default React.memo(TimelineCalendarGrid);
